import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layers, ChevronDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { fetchSimilarTickets } from '@/hooks/useTickets';
import type { TicketDetail, SimilarTicketItem } from '@uniticket/shared';

interface SimilarTicketsPanelProps {
  ticket: TicketDetail;
}

export function SimilarTicketsPanel({ ticket }: SimilarTicketsPanelProps) {
  const { t } = useTranslation('tickets');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<SimilarTicketItem[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetchSimilarTickets({
          subject: ticket.subject,
          description: ticket.description ?? undefined,
          jvid: ticket.jvid ?? undefined,
          dmpOrderId: ticket.dmpOrderId ?? undefined,
          areaId: ticket.areaId ?? undefined,
          ticketType: ticket.ticketType,
          orderSubcategory: ticket.orderSubcategory ?? undefined,
          excludeTicketId: ticket.id,
          limit: 5,
        });
        if (!cancelled) setResults(res.data);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [ticket.id]);

  if (!loading && results.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4"
      >
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-bold text-slate-800">{t('similarTickets.title')}</span>
          {!loading && results.length > 0 && (
            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
              {results.length}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-6 pb-4 space-y-2">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            results.map((r) => (
              <Link
                key={r.id}
                to={`/tickets/${r.id}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono font-semibold text-brand-rose">{r.ticketNumber}</span>
                    <StatusBadge status={r.status} />
                    <span className="text-[10px] font-medium text-slate-400">
                      {t('similarTickets.score', { score: r.score })}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 truncate">{r.subject}</p>
                </div>
                <div className="flex flex-wrap gap-1 shrink-0">
                  {r.matchReasons.slice(0, 3).map((reason) => (
                    <span key={reason} className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                      {t(`similarTickets.matchReasons.${reason}`)}
                    </span>
                  ))}
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
